import { isWorkspacePath } from "./workspaceRoutes";
import { AdminDesignSurface } from "src/shared/ui/src/design/AdminDesignSurface";
import {
  InterfaceDesignSwitch,
  useInterfaceDesign,
} from "src/shared/ui/src/design/InterfaceDesign";
import { Box } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import MenuHeader from "../menu/MenuHeader";
import Footer from "./Footer";
import { useLocation } from "react-router-dom";

// Composant qui gère le layout principal avec header et footer
interface MainLayoutProps {
  children: ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  //get current route
  const location = useLocation();
  const pathname = location.pathname;
  const isLicensed = useMemo(
    () => pathname.startsWith("/licensed"),
    [pathname],
  );
  const isAdmin = useMemo(() => pathname.startsWith("/admin"), [pathname]);
  const hideFooter = isLicensed || isAdmin;
  const { mode } = useInterfaceDesign();
  const designWorkspace = isWorkspacePath(pathname);
  const modern = designWorkspace && mode === "modern";
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    () => window.document.fullscreenElement != null,
  );

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(window.document.fullscreenElement != null);
    };
    window.document.addEventListener(
      "fullscreenchange",
      handleFullscreenChange,
    );
    return () => {
      window.document.removeEventListener(
        "fullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  return (
    <AdminDesignSurface enabled={designWorkspace}>
      <Box
        sx={{
          "--workspace-header-height": modern ? "54px" : "70px",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        {(isAdmin || isLicensed) && <InterfaceDesignSwitch />}
        {!isFullscreen && <MenuHeader />}
        <Box
          component="main"
          sx={{
            flex: 1,
            // Own the scroll: MUI Select/Menu lock body overflow and can reset window.scrollY.
            // Scrolling inside main keeps position stable when menus/snacks open.
            height: isFullscreen
              ? "100vh"
              : "calc(100vh - var(--workspace-header-height, 70px))",
            overflow: "auto",
            // Prevent MenuHeader horizontal shift when scrollbar appears/disappears
            scrollbarGutter: hideFooter ? "stable" : "auto",
            "& > *": {
              // Applique une hauteur minimale à tous les enfants directs (les pages)
              minHeight: isFullscreen
                ? "100vh"
                : "calc(100vh - var(--workspace-header-height, 70px))",
            },
          }}
        >
          {children}
        </Box>
        {!hideFooter && <Footer />}
      </Box>
    </AdminDesignSurface>
  );
}
